import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { postWebPushSendNotification } from "@client/api/endpoints";
import {
  currentPermission,
  ensureSubscribed,
  getSubscription,
  isIos,
  isPushSupported,
  isStandalonePwa,
  registerServiceWorker,
  requestAndSubscribe,
  unsubscribeThisDevice,
} from "@client/lib/push";
import { useUserStore } from "@client/store/user";

/**
 * Bootstrap push na úrovni celé app — volá se v Root.tsx (ne až v Settings).
 * Registruje SW při startu a po přihlášení se pokusí o refresh-on-load self-heal
 * subscription (jen pokud už je permission 'granted').
 */
export const usePushBootstrap = (): void => {
  const user = useUserStore((s) => s.user);

  useEffect(() => {
    void registerServiceWorker();
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") {
      // Zřetězeno: nejdřív (idempotentní) registrace SW, pak refresh-on-load subscribe.
      void registerServiceWorker()
        .then(() => ensureSubscribed())
        .catch(() => undefined);
    }
  }, [user]);
};

/** UI hook pro správu notifikací (Push panel). */
export const useNotification = () => {
  const [permission, setPermission] = useState<NotificationPermission | undefined>(currentPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = isPushSupported();

  const refresh = useCallback(async () => {
    setPermission(currentPermission());
    if (!supported) return;
    const sub = await getSubscription().catch(() => null);
    setSubscribed(!!sub);
  }, [supported]);

  useEffect(() => {
    // Synchronizace s external systémem (Notification API + PushManager) — záměrné.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const res = await requestAndSubscribe();
      setPermission(currentPermission());
      if (res.ok) {
        setSubscribed(true);
        toast.success("Notifikace povoleny na tomto zařízení.");
      } else {
        switch (res.reason) {
          case "denied":
            toast.error("Notifikace jsou zablokované. Povol je v nastavení prohlížeče.");
            break;
          case "unsupported":
            toast.error("Tento prohlížeč push notifikace nepodporuje.");
            break;
          case "no-vapid":
            toast.error("Chybí VAPID klíč (VITE_VAPID_PUBLIC).");
            break;
          case "ios-not-installed":
            toast.error("Na iPhonu/iPadu nejdřív přidej appku na plochu přes Safari (Sdílet → Přidat na plochu), nebo použij ntfy/Telegram níže.");
            break;
          case "push-service-blocked":
            toast.error(
              "Push služba je nedostupná. V Braveu zapni 'Use Google services for push messaging' v brave://settings/privacy a restartuj. Na mobilu zkus ntfy/Telegram níže."
            );
            break;
          case "bad-key":
            toast.error("Neplatný VAPID klíč — kontaktuj správce.");
            break;
          default:
            toast.error("Notifikace nebyly povoleny.");
        }
      }
      await refresh();
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await unsubscribeThisDevice();
      setSubscribed(false);
      toast.success("Notifikace na tomto zařízení vypnuty.");
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    const res = await postWebPushSendNotification();
    if (res.success) toast.success(res.message);
    else toast.error(res.message);
  }, []);

  return {
    permission,
    subscribed,
    supported,
    busy,
    iosNeedsInstall: isIos() && !isStandalonePwa(),
    enable,
    disable,
    sendTest,
    refresh,
    /** Back-compat alias (stávající UserTabPanel) — povolí + subscribne. */
    testNotification: enable,
  };
};
